import { useTranslation } from 'react-i18next';

import { CSSProperties, useEffect, useState } from 'react';

import {
  IconAlertCircle,
  IconBrandLinkedin,
  IconDownload,
  IconEye,
  IconFile,
  IconFileTypeCss,
  IconFileTypeCsv,
  IconFileTypeDoc,
  IconFileTypeDocx,
  IconFileTypeHtml,
  IconFileTypeJs,
  IconFileTypePdf,
  IconFileTypePpt,
  IconFileTypeTxt,
  IconFileTypeXls,
  IconFileTypeXml,
  IconFileTypeZip,
  IconMail,
  IconPaperclip,
  IconPhone,
  IconPhoto,
  IconUserCheck,
} from '@tabler/icons-react';

import { Link, createFileRoute } from '@tanstack/react-router';

import { GanttCore } from '@optiro/gantt';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Container,
  DateDisplayView,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@optiro/ui';

import { AccountAvatar } from '@/components/AccountAvatar';
import { CompanyLogo } from '@/components/CompanyLogo/CompanyLogo';
import { useIntervalCommentsDrawerStore } from '@/hooks/intervalComments/useIntervalCommentsDrawerStore';
import { API_URL } from '@/lib/apiUrl';
import { GanttViewControls } from '@/modules/Planning/Projects/components/GanttViewControls/GanttViewControls';
import type { GanttGroupBy } from '@/modules/Planning/Projects/hooks/usePlanningGroupBy';
import { usePlanningGroupBy } from '@/modules/Planning/Projects/hooks/usePlanningGroupBy';
import { BookingCommentsDrawer } from '@/modules/Samarbejde/BookingCommentsDrawer';
import { ConnectedPartnersChips } from '@/modules/SharedPlanning/components/ConnectedPartnersChips/ConnectedPartnersChips';
import { PartnerClaimDrawer } from '@/modules/SharedPlanning/components/PartnerClaim/PartnerClaimDrawer';
import { SharedResourceFilter } from '@/modules/SharedPlanning/components/SharedResourceFilter';
import { SubscribeButton } from '@/modules/SharedPlanning/components/SubscribeButton';
import {
  useMyResourceableIds,
  useSharedGanttAdapter,
  useSharedProject,
} from '@/modules/SharedPlanning/hooks';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';

import logo from './optiro.svg';

export const Route = createFileRoute('/shared/projects/$sharedProjectId/')({
  component: RouteComponent,
});

function SharedGanttView({
  project,
  settings,
  selectedResourceableIds,
  groupBy,
  onGroupByChange,
  onOpenClaim,
  toolbarTarget,
}: {
  project: Parameters<typeof useSharedGanttAdapter>[0]['project'];
  settings: Parameters<typeof useSharedGanttAdapter>[0]['settings'];
  selectedResourceableIds: number[];
  groupBy: GanttGroupBy;
  onGroupByChange: (groupBy: GanttGroupBy) => void;
  onOpenClaim: () => void;
  toolbarTarget: HTMLElement | null;
}) {
  const { data, milestones, startDate, endDate, config, handlers, renderers, focusDate } =
    useSharedGanttAdapter({ project, settings, selectedResourceableIds, groupBy });

  const commentsIntervalId = useIntervalCommentsDrawerStore((s) => s.intervalId);
  const commentsTaskName = useIntervalCommentsDrawerStore((s) => s.taskName);
  const closeCommentsDrawer = useIntervalCommentsDrawerStore((s) => s.close);

  return (
    <>
      <GanttCore
        data={data}
        milestones={milestones}
        startDate={startDate}
        endDate={endDate}
        config={config}
        handlers={handlers}
        renderers={renderers}
        focusDate={focusDate}
        readOnly
        toolbarTarget={toolbarTarget}
        toolbarSection={<GanttViewControls groupBy={groupBy} onGroupByChange={onGroupByChange} />}
      />
      <BookingCommentsDrawer
        intervalId={commentsIntervalId}
        taskName={commentsTaskName}
        onClose={closeCommentsDrawer}
        onUnauthorizedCta={() => {
          closeCommentsDrawer();
          onOpenClaim();
        }}
      />
    </>
  );
}

function RouteComponent() {
  const { t } = useTranslation('partnerClaim');
  const { t: tGantt } = useTranslation('gantt');
  const { sharedProjectId } = Route.useParams();
  const { data, isLoading, error } = useSharedProject({ token: sharedProjectId });
  const { data: myResourceableIds } = useMyResourceableIds({ token: sharedProjectId });
  const [selectedResourceableIds, setSelectedResourceableIds] = useState<number[]>([]);
  const [claimDrawerOpened, setClaimDrawerOpened] = useState(false);
  const { groupBy, setGroupBy } = usePlanningGroupBy();
  const { isSignedIn, userData } = useAuth();
  // Callback refs (not useRef) — this component early-returns while loading/erroring,
  // so the header/sentinel nodes only exist once `data` resolves. A useRef + useEffect([])
  // pair would attach before that node ever mounts and never re-attach.
  const [pageHeaderEl, setPageHeaderEl] = useState<HTMLDivElement | null>(null);
  const [pageHeaderHeight, setPageHeaderHeight] = useState(0);
  const [ganttSentinelEl, setGanttSentinelEl] = useState<HTMLDivElement | null>(null);
  const [isCompactHeader, setIsCompactHeader] = useState(false);
  const [headerToolbarSlotEl, setHeaderToolbarSlotEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pageHeaderEl) return;
    const observer = new ResizeObserver(([entry]) => setPageHeaderHeight(entry.contentRect.height));
    observer.observe(pageHeaderEl);
    return () => observer.disconnect();
  }, [pageHeaderEl]);

  // Swap the logo for the project name + filters once the Gantt section
  // scrolls up under the sticky header.
  useEffect(() => {
    if (!ganttSentinelEl) return;
    const observer = new IntersectionObserver(
      ([entry]) =>
        setIsCompactHeader(
          !entry.isIntersecting && entry.boundingClientRect.top < pageHeaderHeight
        ),
      { rootMargin: `-${pageHeaderHeight}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(ganttSentinelEl);
    return () => observer.disconnect();
  }, [ganttSentinelEl, pageHeaderHeight]);

  // Below Ressourcer the link sends no bookinger, so there is nothing to filter.
  const showsResources = (data?.data.layers ?? 'resources') === 'resources';
  const showingOnlyMyBookings =
    !!myResourceableIds &&
    myResourceableIds.length > 0 &&
    selectedResourceableIds.length === myResourceableIds.length &&
    myResourceableIds.every((id) => selectedResourceableIds.includes(id));

  useEffect(() => {
    if (data?.data?.name) {
      document.title = `${data.data.name} - Delt projektplan | Optiro`;
    } else {
      document.title = 'Delt projektplan | Optiro';
    }
  }, [data]);

  const getFileIcon = (ext: string) => {
    switch (ext.toLowerCase()) {
      case 'pdf':
        return <IconFileTypePdf size={14} />;
      case 'csv':
        return <IconFileTypeCsv size={14} />;
      case 'doc':
        return <IconFileTypeDoc size={14} />;
      case 'docx':
        return <IconFileTypeDocx size={14} />;
      case 'xls':
      case 'xlsx':
        return <IconFileTypeXls size={14} />;
      case 'ppt':
      case 'pptx':
        return <IconFileTypePpt size={14} />;
      case 'txt':
        return <IconFileTypeTxt size={14} />;
      case 'zip':
      case 'rar':
      case '7z':
        return <IconFileTypeZip size={14} />;
      case 'html':
      case 'htm':
        return <IconFileTypeHtml size={14} />;
      case 'css':
        return <IconFileTypeCss size={14} />;
      case 'js':
      case 'jsx':
        return <IconFileTypeJs size={14} />;
      case 'xml':
        return <IconFileTypeXml size={14} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
        return <IconPhoto size={14} />;
      default:
        return <IconFile size={14} />;
    }
  };

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error || !data) {
    return (
      <Container size="sm" mt="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Link ikke tilgængeligt" color="red">
          Dette delingslink er enten udløbet, inaktivt eller eksisterer ikke. Kontakt projektlederen
          for et nyt link.
        </Alert>
      </Container>
    );
  }

  return (
    <div
      style={
        {
          minHeight: '100vh',
          backgroundColor: 'rgb(248, 245, 239)',
          '--app-shell-header-height': `${pageHeaderHeight}px`,
        } as CSSProperties
      }
    >
      <Box
        ref={setPageHeaderEl}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderBottom: '1px solid #e9ecef',
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <Container size="xl">
          <Group justify="space-between" py="md" wrap="nowrap">
            <Box
              style={{
                position: 'relative',
                flex: 1,
                minWidth: 0,
                height: 32,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Box
                style={{
                  position: 'absolute',
                  left: 0,
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isCompactHeader ? 0 : 1,
                  transform: isCompactHeader
                    ? 'translateY(-8px) scale(0.98)'
                    : 'translateY(0) scale(1)',
                  transition:
                    'opacity 260ms cubic-bezier(.4,0,.2,1), transform 260ms cubic-bezier(.4,0,.2,1)',
                  pointerEvents: isCompactHeader ? 'none' : 'auto',
                }}
              >
                {data.data.company?.logo_url ? (
                  <Group gap={8} align="center" wrap="nowrap">
                    <CompanyLogo
                      logoUrl={data.data.company.logo_url}
                      name={data.data.company.name}
                      size={32}
                    />
                    <Anchor
                      href="https://optiro.dk"
                      target="_blank"
                      style={{ textDecoration: 'none', opacity: 0.45 }}
                      title="Bygget med Optiro"
                    >
                      <Image src={logo} alt="Optiro" h={14} w="auto" />
                    </Anchor>
                  </Group>
                ) : (
                  <Anchor
                    href="https://optiro.dk"
                    target="_blank"
                    style={{ textDecoration: 'none' }}
                  >
                    <Image src={logo} alt="Optiro" h={32} w="auto" />
                  </Anchor>
                )}
              </Box>
              <Box
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  opacity: isCompactHeader ? 1 : 0,
                  transform: isCompactHeader
                    ? 'translateY(0) scale(1)'
                    : 'translateY(8px) scale(0.98)',
                  transition:
                    'opacity 260ms cubic-bezier(.4,0,.2,1) 60ms, transform 260ms cubic-bezier(.4,0,.2,1) 60ms',
                  pointerEvents: isCompactHeader ? 'auto' : 'none',
                }}
              >
                <Text fw={700} truncate style={{ maxWidth: 260, flexShrink: 0 }}>
                  {data.data.name}
                </Text>
                <Group gap="sm" wrap="nowrap" style={{ overflow: 'hidden' }}>
                  <SharedResourceFilter
                    resources={data.data.resources}
                    selectedResourceableIds={selectedResourceableIds}
                    onFilterChange={setSelectedResourceableIds}
                  />
                  <Button
                    variant={showingOnlyMyBookings ? 'light' : 'subtle'}
                    color="gray"
                    size="sm"
                    leftSection={<IconUserCheck size={14} />}
                    display={showsResources ? undefined : 'none'}
                    onClick={() => {
                      if (myResourceableIds && myResourceableIds.length > 0) {
                        setSelectedResourceableIds(showingOnlyMyBookings ? [] : myResourceableIds);
                      } else {
                        setClaimDrawerOpened(true);
                      }
                    }}
                  >
                    {tGantt('my_bookings_filter')}
                  </Button>
                </Group>
                <div
                  ref={setHeaderToolbarSlotEl}
                  style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                />
              </Box>
            </Box>
            <Group gap="lg">
              <SubscribeButton token={sharedProjectId} iconOnly />
              {isSignedIn && userData && (
                <Anchor
                  component={Link}
                  to="/app/dashboard"
                  style={{ textDecoration: 'none' }}
                  c="dark"
                >
                  <Group gap="xs">
                    <AccountAvatar
                      photoUrl={userData.photo.url}
                      name={userData.name}
                      radius="xl"
                      size="sm"
                    />
                    <Text size="sm" fw={500}>
                      {userData.company.name}
                    </Text>
                  </Group>
                </Anchor>
              )}
              {!isSignedIn && (
                <Button variant="outline" color="dark" onClick={() => setClaimDrawerOpened(true)}>
                  {t('cta')}
                </Button>
              )}
            </Group>
          </Group>
        </Container>
      </Box>

      <Container size="xl" py="xl">
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap">
            <div>
              <Group gap="xs" mb="xs">
                <Text size="2rem" fw={700} style={{ lineHeight: 1.2 }}>
                  {data.data.name}
                </Text>
                <Badge variant="dot" color="green" size="lg">
                  Live
                </Badge>
              </Group>
              {data.data.client_name && (
                <Text size="md" c="dimmed">
                  {data.data.client_name}
                </Text>
              )}
              <div style={{ marginTop: 8 }}>
                <ConnectedPartnersChips partners={data.data.connected_partners} />
              </div>
            </div>
          </Group>

          <Group gap="sm" wrap="wrap">
            <SharedResourceFilter
              resources={data.data.resources}
              selectedResourceableIds={selectedResourceableIds}
              onFilterChange={setSelectedResourceableIds}
            />
            <Button
              variant={showingOnlyMyBookings ? 'light' : 'subtle'}
              color="gray"
              size="sm"
              leftSection={<IconUserCheck size={14} />}
              display={showsResources ? undefined : 'none'}
              onClick={() => {
                if (myResourceableIds && myResourceableIds.length > 0) {
                  setSelectedResourceableIds(showingOnlyMyBookings ? [] : myResourceableIds);
                } else {
                  setClaimDrawerOpened(true);
                }
              }}
            >
              {tGantt('my_bookings_filter')}
            </Button>
          </Group>

          <div ref={setGanttSentinelEl} />

          <div
            className="md:relative md:left-1/2 md:right-1/2 md:-ml-[50vw] md:-mr-[50vw] md:w-screen md:px-4"
            style={{ backgroundColor: 'rgb(248, 245, 239)' }}
          >
            <div>
              <SharedGanttView
                project={data.data}
                settings={data.settings}
                selectedResourceableIds={selectedResourceableIds}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                onOpenClaim={() => setClaimDrawerOpened(true)}
                toolbarTarget={headerToolbarSlotEl}
              />
            </div>
          </div>

          {data.settings.show_files && data.data.files && data.data.files.length > 0 && (
            <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
              <Group
                gap="xs"
                px="md"
                py="sm"
                style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}
              >
                <ThemeIcon size="sm" variant="transparent" color="gray">
                  <IconPaperclip size={16} />
                </ThemeIcon>
                <Text size="sm" fw={600}>
                  Filer
                </Text>
                <Badge size="xs" variant="light" color="gray">
                  {data.data.files.length}
                </Badge>
              </Group>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Filnavn</Table.Th>
                    <Table.Th>Uploadet</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.data.files.map((file) => (
                    <Table.Tr key={file.id}>
                      <Table.Td>
                        <Group gap="xs">
                          <ThemeIcon variant="light" color="blue" size="sm" radius="xl">
                            {getFileIcon(file.file_extension)}
                          </ThemeIcon>
                          <Text size="sm">{file.file_name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          <DateDisplayView date={file.created_at} />
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group justify="flex-end" gap={4}>
                          <Tooltip label="Åbn fil" withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() => window.open(file.url, '_blank')}
                            >
                              <IconEye size={15} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Download" withArrow>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={async () => {
                                const proxyUrl = `${API_URL}/api/v1/public/shared_projects/${sharedProjectId}/files/${file.id}`;
                                const res = await fetch(proxyUrl);
                                const blob = await res.blob();
                                const blobUrl = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = file.file_name;
                                a.click();
                                URL.revokeObjectURL(blobUrl);
                              }}
                            >
                              <IconDownload size={15} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          <Paper p="md" radius="md" withBorder>
            <Group justify="space-between" align="center" wrap="wrap">
              <div style={{ maxWidth: '600px' }}>
                <Text size="sm" c="dimmed" mb={4}>
                  Vil du også have professionel projektstyring?
                </Text>
                <Text size="xs" c="dimmed">
                  Optiro hjælper med intelligent planlægning og ressourcestyring.
                </Text>
              </div>
              <Group gap="xs">
                <Button
                  size="sm"
                  variant="filled"
                  style={{ backgroundColor: '#3c735b' }}
                  component="a"
                  href="https://app.optiro.dk/experiences/login"
                  target="_blank"
                >
                  Start nu
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  color="gray"
                  component="a"
                  href="https://optiro.dk"
                  target="_blank"
                >
                  Læs mere
                </Button>
              </Group>
            </Group>
          </Paper>

          <Group justify="center" gap="xl" py="md">
            <Anchor
              size="sm"
              c="dimmed"
              href="mailto:support@optiro.dk"
              target="_blank"
              style={{ textDecoration: 'none' }}
            >
              <Group gap={6}>
                <IconMail size={16} />
                support@optiro.dk
              </Group>
            </Anchor>
            <Anchor
              size="sm"
              c="dimmed"
              href="tel:+4526122824"
              target="_blank"
              style={{ textDecoration: 'none' }}
            >
              <Group gap={6}>
                <IconPhone size={16} />
                +45 26 12 28 24
              </Group>
            </Anchor>
            <Anchor
              size="sm"
              c="dimmed"
              href="https://linkedin.com/company/optiro-planning"
              target="_blank"
              style={{ textDecoration: 'none' }}
            >
              <Group gap={6}>
                <IconBrandLinkedin size={16} />
                LinkedIn
              </Group>
            </Anchor>
          </Group>
        </Stack>
      </Container>
      <PartnerClaimDrawer
        opened={claimDrawerOpened}
        onClose={() => setClaimDrawerOpened(false)}
        token={sharedProjectId}
      />
    </div>
  );
}
